import dayjs from "dayjs";

import type {
  AlvysConnection,
  AlvysConnectionUpdate,
  AlvysKeyStatus,
  AlvysSecret
} from "models/Alvys";

export interface AlvysFormValues {
  clientId: string;
  clientSecret: string;
  webhookSecret: string;
  keyExpiresOn: string;
  newKeyExpiresOn: string;
}

export interface RenewalNotice {
  variant: "warning" | "error";
  title: string;
  message: string;
}

const PICKER_DATE_FORMAT = "MM/DD/YYYY";
const API_DATE_FORMAT = "YYYY-MM-DD";
const RENEWAL_STATUSES: AlvysKeyStatus[] = ["expiring_soon", "expired", "rejected"];

export const formatDay = (value: string) => dayjs(value).format("MMM D, YYYY");

export const pacificToday = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date());

export const maskedSecret = (secret: AlvysSecret) => `••••${secret.last4}`;

export const formValuesFrom = (connection: AlvysConnection): AlvysFormValues => ({
  clientId: connection.client_id,
  clientSecret: "",
  webhookSecret: "",
  keyExpiresOn: connection.key_expires_on
    ? dayjs(connection.key_expires_on).format(PICKER_DATE_FORMAT)
    : "",
  newKeyExpiresOn: ""
});

const apiDate = (value: string) => (value ? dayjs(value).format(API_DATE_FORMAT) : null);

export const isNewKey = (connection: AlvysConnection, values: AlvysFormValues) =>
  Boolean(values.clientSecret) || values.clientId.trim() !== connection.client_id;

export const changedValues = (
  connection: AlvysConnection,
  values: AlvysFormValues
): AlvysConnectionUpdate => {
  const update: AlvysConnectionUpdate = {};
  const clientId = values.clientId.trim();
  if (clientId !== connection.client_id) update.client_id = clientId;
  if (values.clientSecret) update.client_secret = values.clientSecret;
  if (values.webhookSecret) update.webhook_secret = values.webhookSecret;
  if (isNewKey(connection, values)) {
    update.key_expires_on = apiDate(values.newKeyExpiresOn);
    return update;
  }
  const keyExpiresOn = apiDate(values.keyExpiresOn);
  if (keyExpiresOn !== connection.key_expires_on) update.key_expires_on = keyExpiresOn;
  return update;
};

export const clientIdMissing = (connection: AlvysConnection, values: AlvysFormValues) =>
  isNewKey(connection, values) && !values.clientId.trim();

export const clientSecretMissing = (connection: AlvysConnection, values: AlvysFormValues) => {
  const clientId = values.clientId.trim();
  return Boolean(clientId) && clientId !== connection.client_id && !values.clientSecret;
};

export const hasCredentials = (connection: AlvysConnection) =>
  Boolean(
    connection.client_id ||
      connection.client_secret.configured ||
      connection.webhook_secret.configured
  );

export const needsRenewal = (connection: AlvysConnection) =>
  RENEWAL_STATUSES.includes(connection.key_status);

export const renewalNotice = (connection: AlvysConnection): RenewalNotice | null => {
  switch (connection.key_status) {
    case "expiring_soon":
      return {
        variant: "warning",
        title: `Your Alvys key expires on ${formatDay(connection.key_expires_on ?? "")}`,
        message:
          "Create a new key in Alvys and paste it here. Your current key keeps working until it expires, so renewing now causes no gap."
      };
    case "expired":
      return {
        variant: "error",
        title: "Your Alvys key has expired",
        message:
          "Loads can't sync to HaulPay until you create a new key in Alvys and paste it here."
      };
    case "rejected":
      return {
        variant: "error",
        title: "Alvys refused your key",
        message:
          "Loads can't sync to HaulPay until you create a new key in Alvys and paste it here. The key may have expired or been deleted in Alvys."
      };
    default:
      return null;
  }
};

export const keySummary = (connection: AlvysConnection) => {
  if (connection.key_status === "not_configured") return "No key yet";
  if (!connection.key_expires_on) return "No expiry date set";
  const day = formatDay(connection.key_expires_on);
  return connection.key_status === "expired" ? `Expired ${day}` : `Expires ${day}`;
};

export const connectionSummary = (connection: AlvysConnection) => {
  const hasKey = connection.key_status !== "not_configured";
  if (hasKey && connection.last_event_at) {
    return {
      isConnected: true,
      label: "Connected",
      detail: `Last event ${dayjs(connection.last_event_at).format("MMM D, YYYY h:mm A")}`
    };
  }
  if (connection.last_event_at) {
    return {
      isConnected: false,
      label: "Not connected",
      detail: "No API key saved. Add it in step 2 so loads can sync."
    };
  }
  return {
    isConnected: false,
    label: "Not connected yet",
    detail: "No events received from Alvys yet"
  };
};
